import { useState } from 'react';
import {
  Button,
  Grid,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
} from '@mui/material';
import RetailerLookupButton from '../retailerLookupButton.js';
import axios from 'axios';
import GameSelectModal from './gamesSelectModal.js';

const TexasScratchTicketForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: 'Texas',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    callerName: '',
    comments: '',
    games: [],
    zoneId: null,
    latitude: null,
    longitude: null,
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGameSelectModalOpen, setIsGameSelectModalOpen] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleGameSelectModalOpenClose = () => {
    setIsGameSelectModalOpen(!isGameSelectModalOpen);
  };

  const handleClear = () => {
    setForm({
      ...form,
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      zoneId: null,
      latitude: null,
      longitude: null,
      callerName: '',
      comments: '',
      games: [], // Clear game selections
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      const gamesWithQuantity = (form.games || []).filter(
        games => games.quantity != null && games.quantity !== '',
      );

      const payload = {
        ...form,
        games: gamesWithQuantity,
      };

      await axios({
        method: 'POST',
        url: `${process.env.REACT_APP_ENDPOINT_FORMS}/uscc/texasScratchTicket/submit`,
        data: payload,
      });

      setSnackbarMessage('Form submitted successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error submitting form:', error);
      setSnackbarMessage(
        'There was an error submitting the form. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      jurisdiction,
      retailerId,
      retailerName,
      address,
      phoneNumber,
      callerName,
    } = form;

    if (
      !jurisdiction ||
      !retailerId ||
      !retailerName ||
      !address ||
      !phoneNumber ||
      !callerName
    ) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label={translations.jurisdiction || 'Jurisdiction'}
            name='jurisdiction'
            required
            disabled
            value={form.jurisdiction}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Retailer Name'
            name='retailerName'
            required
            value={form.retailerName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            label='Phone Number'
            name='phoneNumber'
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phoneNumber}
            onChange={handleChange}
            required
          />
          <Button
            variant={'contained'}
            disabled={!form.jurisdiction}
            onClick={handleGameSelectModalOpenClose}
            sx={{
              backgroundColor: 'var(--procyon-green)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            Select Games
          </Button>
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Tooltip
            title={'Enter a Retailer ID (numeric values only) to lookup'}
            arrow
          >
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Address'
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            required
            label='Caller Name'
            name='callerName'
            fullWidth
            value={form.callerName}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
          <TextField
            label='Comments'
            name='comments'
            value={form.comments}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              !isFormValid()
                ? 'Please fill in all required fields to enable the Submit button'
                : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
      {isGameSelectModalOpen && (
        <GameSelectModal
          isOpen={isGameSelectModalOpen}
          handleClose={handleGameSelectModalOpenClose}
          setForm={setForm}
          games={form.games}
        />
      )}
    </>
  );
};

export default TexasScratchTicketForm;
